import { createContext, useContext, useEffect, useState, memo } from "react";
import { auth, secondaryAuth } from "../utils/init-firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  getAuth,
  sendEmailVerification,
  updatePassword,
} from "firebase/auth";
import { Timestamp } from "firebase/firestore";
import UsersService from "../services/Users.Service";
import { getUrlDetails } from "../utils/UrlDomainUtil";
import { GetPortal } from "../utils/urlPortalUtil";
import GenericService from "../services/Generic.service";
import UsersGroupsService from "../services/UsersGroups.service";
import { isCpf } from "validator-brazil";

const AuthContext = createContext({
  currentUser: null,
  domainDetails: null,
  changePortal: () => Promise,
  register: () => Promise,
  login: () => Promise,
  logout: () => Promise,
  existEmail: () => Promise,
  changePassword: () => Promise,
  changeEmail: () => Promise,
  changeName: () => Promise,
});

export const useAuth = () => useContext(AuthContext);

function AuthContextProviderComponent({ children }) {

  const [domainDetails, setDomainDetails] = useState(null);

  const [currentUser, setCurrentUser] = useState({});

  useEffect(() => {
    if (domainDetails === null) {
      getUrlDetails().then((urlDetails) => {
        setDomainDetails(urlDetails);
      });
    }
  }, [domainDetails]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      try {
        if (!user) {
          setCurrentUser(undefined);
          refreshDomainDetails();
          return;
        }

        if (!user.uid) {
          setCurrentUser(undefined);
          refreshDomainDetails();
          return;
        }

        getUrlDetails().then(async (urlDetails) => {
          if (!urlDetails) {
            setCurrentUser(undefined);
            refreshDomainDetails();
            return;
          }

          setDomainDetails(urlDetails);

          // Determinar o ID atual do usuário
          let currentID = user.uid;

          if (
            //   //   //   // urlDetails.domainId === "4X8eTXc3fCjlGFQPJ3gi" && 
            //   //   //   // urlDetails.domainId === "4sy7BRerrFlj1emAaeMd" &&
            // urlDetails.domainId === "b8FTkdrlN8OmmOnxFT3v" &&
            window.location.href.includes("https://preview-sg-cbtkd.web.app/transparencia") &&
            currentID === "TRhRghIYvkTs5igtOt6YnoQGAvC3") {
            // currentID = "SMGiNhoG6qd88bxRJTXmRWSUn0C3";
            // currentID = "2bHT71T2KTQ4I8e2jc3d"; // Cleiton
            // currentID = "FPZbKX5jxoPsPpwWx7w2BUlA5Ky2";
            // currentID = "QNflZa2Q4fNOUeOzzkYgDrpp9nN2"; //GABRIELA
            currentID = "K0u06zpFb1cMg1MmX6Svz1olIWi2";
          }

          try {
            // Buscar as permissões do usuário no portal atual
            const rulesOwnedRetrieved = await UsersService.getRulesOwnedUnderPortalID(currentID, urlDetails.domainId);

            // Buscar configurações padrão
            const configuracoesRetrieved = await GenericService.getOne("configuracoes", "padrao");
            // console.log("configuracoesRetrieved.standardRules=", configuracoesRetrieved.standardRules);

            // Buscar informações do praticante
            const praticanteRetrieved = await GenericService.getOne("praticantes", currentID);
            // console.log("authContext => Passo 1 => currentID=", currentID);

            // Configurar o usuário com as informações base
            let userConfig = {
              ...user,
              uid: currentID,
              rulesOwned: rulesOwnedRetrieved
            };

            // Adicionar informações de empregador se disponíveis
            if (praticanteRetrieved?.dadosColaborador?.administrativo?.idEmpregador) {
              userConfig.idEmpregador = praticanteRetrieved.dadosColaborador.administrativo.idEmpregador;
              userConfig.nomeEmpregador = praticanteRetrieved.dadosColaborador.administrativo.nomeEmpregador;
              // console.log("authContext => Passo 2");
            }

            // Verificar se o praticante pertence à federação atual e tem graduação relevante
            const pertenceFederacao = praticanteRetrieved?.dadosFiliacao?.federacaoID === urlDetails.domainId;

            if (pertenceFederacao && praticanteRetrieved?.dadosTaekwondo) {
              // console.log("authContext => Passo 3");
              const graduacao = praticanteRetrieved.dadosTaekwondo.maxGraduacao;

              try {
                // Adicionar regras especiais baseadas na graduação
                if (graduacao >= 14) {
                  const userGroupMestres = await UsersGroupsService.getOne(configuracoesRetrieved.standardRules.mestres);
                  // console.log("userGroupMestres.rulesowned=", userGroupMestres.rulesowned);
                  userConfig.rulesOwned = [...new Set([...rulesOwnedRetrieved, ...userGroupMestres.rulesowned])];
                }
                else if (graduacao > 10) {
                  const userGroupsProfessores = await UsersGroupsService.getOne(configuracoesRetrieved.standardRules.professores);
                  // console.log("userGroupsProfessores.rulesowned=", userGroupsProfessores.rulesowned);
                  userConfig.rulesOwned = [...new Set([...rulesOwnedRetrieved, ...userGroupsProfessores.rulesowned])];
                }
              } catch (error) {
                console.log("Erro ao carregar regras especiais:", error);
                // Mantém as regras básicas em caso de erro
              }
            }

            // Atualizar o estado do usuário atual
            setCurrentUser(userConfig);

          } catch (error) {
            console.log("Erro ao buscar informações do usuário:", error);
            setCurrentUser({ ...user, uid: currentID, rulesOwned: [] });
          }
        });
      } catch (err) {
        console.log("Erro na verificação de autenticação:", err);
        setCurrentUser(undefined);
        refreshDomainDetails();
      }
    });

    return () => unsubscribe();
  }, []);

  // Função auxiliar para atualizar detalhes do domínio
  const refreshDomainDetails = () => {
    getUrlDetails().then((urlDetails) => {
      setDomainDetails(urlDetails);
    });
  };

  const changePortal = async (portalName) => {
    try {
      // Verificar se domainDetails existe antes de prosseguir
      if (!domainDetails) {
        console.log("domainDetails não está definido");
        return false;
      }

      let newPortal = await GetPortal();
      
      // Verificar se newPortal foi retornado corretamente
      if (!newPortal) {
        console.log("Erro ao obter novo portal");
        return false;
      }

      // Atualizar domainDetails
      setDomainDetails({
        domain: domainDetails.domain,
        domainId: domainDetails.domainId,
        subDomain: domainDetails.subDomain,
        portal: newPortal.urlportal,
        portalId: newPortal.id, // Corrigido para minúsculo (id em vez de Id)
      });

      return true;
    } catch (error) {
      console.log("Erro ao mudar de portal:", error);
      return false;
    }
  };


  const existEmail = async (email) => {
    return email !== undefined && email !== null && email !== "";
  };

  const changePassword = async (lastPassword, newPassword) => {
    const user = getAuth().currentUser;
    try {
      await signOut(auth);
      const userCredential = await signInWithEmailAndPassword(auth, user.email, lastPassword);
      await updatePassword(userCredential.user, newPassword);
      return true;
    } catch (error) {
      console.log("Erro ao alterar senha:", error);
      return false;
    }
  };

  const changeEmail = async (lastPassword, newEmail) => {
    const user = getAuth().currentUser;
    try {
      await signOut(auth);
      const userCredential = await signInWithEmailAndPassword(auth, user.email, lastPassword);
      if (userCredential) {
        return await UsersService.updateEmail(user.uid, newEmail);
      }
      return false;
    } catch (error) {
      console.log("Erro ao alterar email:", error);
      return false;
    }
  };

  const changeName = async (lastPassword, newName) => {
    const user = getAuth().currentUser;
    try {
      await signOut(auth);
      const userCredential = await signInWithEmailAndPassword(auth, user.email, lastPassword);
      if (userCredential) {
        return await UsersService.updateName(user.uid, newName);
      }
      return false;
    } catch (error) {
      console.log("Erro ao alterar nome:", error);
      return false;
    }
  };

  const signup = async (userName, email, password) => {
    try {
      const user = await createUserWithEmailAndPassword(auth, email, password);

      const newUser = {
        dadosPessoais: {
          name: userName,
        },
        dadosContato: {
          email: email,
          celular: "",
        },
        metadados: {
          ownerid: user.user.uid ? user.user.uid : "",
          owner: userName ? userName : "",
          creation: Timestamp.fromDate(new Date()),
          date: Timestamp.fromDate(new Date()),
          version: 1,
          active: true,
        },
        usersgroupsowned: [],
      };

      await UsersService.createWithID(user.user.uid, newUser);

      await updateProfile(user.user, {
        displayName: userName,
        photoURL: "",
      });

      // Verificação de email está desabilitada desde 10/10/2023

      return user;
    } catch (error) {
      console.log("Erro ao criar usuário:", error);
      throw error;
    }
  };

  const signupWithoutLogoutCurrentUser = async (userName, email, password) => {
    let returnUser = undefined;
    return await createUserWithEmailAndPassword(secondaryAuth, email, password)
      .then(async (user) => {
        // //console.log("user=", user);
        returnUser = user;
        let newUser = {
          dadosPessoais: {
            name: userName,
          },
          dadosContato: {
            email: email,
            celular: "",
          },
          metadados: {
            ownerid: user.user.uid ? user.user.uid : "",
            owner: userName ? userName : "",
            creation: Timestamp.fromDate(new Date()),
            date: Timestamp.fromDate(new Date()),
            version: 1,
            active: true,
          },
          usersgroupsowned: [],
        };
        await UsersService.createWithID(user.user.uid, newUser);
        return await updateProfile(user.user, {
          displayName: userName,
          photoURL: "",
        })
          .then(async () => {
            const actionCodeSettings = {
              continueUrl: window.location.protocol + "//" + domainDetails.domain + "/signup-validation",
              url: window.location.protocol + "//" + domainDetails.domain + "/signup-validation",
              handleCodeInApp: false,
            };
            if (false) {
              //Desabilitado em 10/10/2023 por Fernando Villela para impedir envio de email de verificação, por enquanto.
              await sendEmailVerification(user.user, actionCodeSettings)
                .then(() => { })
                .catch((error) => {
                  console.log(error);
                });
            }
            return returnUser;
          })
          .catch((error) => {
            console.log(error);
          });
      })
      .catch((error) => {
        if (error.code === "auth/email-already-in-use") {
          //console.log(error);
          throw new Error("Este e-mail já está em uso!");
        } else {
          //console.log(error);
          throw new Error("Erro ao criar usuário!");
        }
      });
  };

  const login = async (email, password) => {
    try {
      // Buscar email por CPF se necessário
      if (isCpf(email)) {
        const cpf = email
          .replace(/[\s.-]/g, "") // Remove espaços, pontos e traços
          .replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4"); // Formata CPF

        const conditionsForAPI = [
          { field: "dadosPessoais.cpf", operator: "==", value: cpf },
          { field: "metadados.successorId", operator: "==", value: "" }
        ];

        const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'https://cbtkd.org.br';

        try {
          const response = await fetch(`${API_URL}/api/generic/getCollectionByConditions`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              collectionName: "praticantes",
              conditions: conditionsForAPI
            })
          });

          const returnedEmail = await response.json();

          if (returnedEmail.totalCount > 0) {
            email = returnedEmail.data[0].dadosContato.email;
          } else {
            throw new Error("CPF não encontrado.");
          }
        } catch (error) {
          console.error("Erro ao buscar dados da API:", error);
          throw new Error("CPF não encontrado ou inválido.");
        }
      }

      // Realizar login
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Verificar email se necessário
      if (!userCredential.user.emailVerified) {
        reSendEmailVerification(userCredential);
      }

      // Verificar cadastros em paralelo para melhor performance
      const [userResult, praticanteResult] = await Promise.allSettled([
        UsersService.getOne(userCredential.user.uid),
        GenericService.getOne("praticantes", userCredential.user.uid)
      ]);

      const cadastroUsuarioCompleto = userResult.status === 'fulfilled' && userResult.value !== undefined;
      const cadastroPraticanteCompletoOK = praticanteResult.status === 'fulfilled' && praticanteResult.value !== undefined;

      return cadastroUsuarioCompleto && cadastroPraticanteCompletoOK;

    } catch (error) {
      console.error("Erro no login:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      return true;
    } catch (error) {
      console.log("Erro ao fazer logout:", error);
      return false;
    }
  };

  const forgetPasswordSendEmail = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  const updateUserProfile = (auth, userDisplayName, userPhotoURL) => {
    return updateProfile(auth, {
      displayName: userDisplayName,
      photoURL: userPhotoURL,
    }).catch((error) => {
      //console.log(error);
    });
  };

  const reSendEmailVerification = async (auth) => {
    // Função simplificada - verificação de email está desabilitada desde 10/10/2023
    return true;

    /*
    const mauth = getAuth();
    const actionCodeSettings = {
      url: "https://sg-cbtkd.web.app/signup-validation",
      handleCodeInApp: false,
    };
    
    if (false) { // Desabilitado em 10/10/2023 por Fernando Villela
      try {
        await sendEmailVerification(mauth.currentUser, actionCodeSettings);
      } catch (error) {
        console.log("Erro ao enviar email de verificação:", error);
      }
    }
    return true;
    */
  };

  // const updateCelular = async (auth, userPhoneNumber) => {
  //   // 'recaptcha-container' is the ID of an element in the DOM.
  //   const applicationVerifier = new RecaptchaVerifier("recaptcha-container");
  //   const provider = new PhoneAuthProvider(auth);
  //   const verificationId = await provider.verifyPhoneNumber(
  //     userPhoneNumber,
  //     applicationVerifier
  //   );
  //   // Obtain the verificationCode from the user.
  //   const phoneCredential = PhoneAuthProvider.credential(
  //     verificationId,
  //     verificationCode
  //   );
  //   return await updatePhoneNumber(auth, phoneCredential).catch((error) => {
  //     //console.log(error);
  //   });

  //   // return updatePhoneNumber(auth, {
  //   //   displayName: userDisplayName,
  //   //   photoURL: userPhotoURL,
  //   // }).catch((error) => {
  //   //   //console.log(error);
  //   // });
  // };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        domainDetails,
        changePortal,
        signup,
        signupWithoutLogoutCurrentUser,
        login,
        logout,
        existEmail,
        forgetPasswordSendEmail,
        updateUserProfile,
        reSendEmailVerification,
        changePassword,
        changeEmail,
        changeName,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const AuthContextProvider = memo(AuthContextProviderComponent);
